import { deserializeProps } from './serialize'
import type {
  BridgeMessage,
  BridgeResponse,
  ChildBridge,
  ConnectBridgeOptions,
  FunctionErrorPayload,
  FunctionResultPayload,
  InvokeFunctionPayload,
} from './types'
import { MessageType } from './types'
import { createLogger, generateId, isBridgeMessage, withTimeout } from './utils'

const DEFAULT_TIMEOUT = 10_000

declare global {
  interface Window {
    xprops: Record<string, unknown> & {
      onProps?: (cb: (props: Record<string, unknown>) => void) => void
    }
  }
}

export async function connectBridge(options: ConnectBridgeOptions): Promise<ChildBridge> {
  const { tag, autoResize, timeout = DEFAULT_TIMEOUT, debug = false } = options
  const logger = createLogger(debug, tag)

  const parentWindow = window.parent !== window ? window.parent : window.opener
  if (!parentWindow) {
    throw new Error('[@afterpaytouch/bridge] Not in an iframe or popup context')
  }

  // Derive parent origin from referrer or use wildcard
  let parentOrigin: string
  try {
    parentOrigin = new URL(document.referrer).origin
  } catch {
    parentOrigin = '*'
  }

  // Pending function invocation calls (child -> parent -> child round-trip)
  const pendingCalls = new Map<string, { resolve: (value: unknown) => void; reject: (error: Error) => void }>()

  // Prop update listeners
  const updateListeners: Array<(props: Record<string, unknown>) => void> = []
  let currentProps: Record<string, unknown> = {}

  function invoke(funcId: string, args: unknown[]): Promise<unknown> {
    const callId = generateId()
    const message: BridgeMessage = {
      __afterpay_bridge__: true,
      tag,
      id: generateId(),
      type: MessageType.INVOKE_FUNCTION,
      payload: { funcId, callId, args } satisfies InvokeFunctionPayload,
      timestamp: Date.now(),
    }

    return new Promise((resolve, reject) => {
      pendingCalls.set(callId, { resolve, reject })
      parentWindow!.postMessage(message, parentOrigin)
      logger.debug('Invoked function', funcId, 'callId', callId)
    })
  }

  function applyProps(serialized: Record<string, unknown>): void {
    currentProps = deserializeProps(serialized, invoke)

    // Set window.xprops for backward compatibility
    window.xprops = {
      ...currentProps,
      onProps: (cb: (props: Record<string, unknown>) => void) => {
        updateListeners.push(cb)
      },
    }
  }

  // Set up auto-resize observer
  let resizeObserver: ResizeObserver | null = null

  function setupAutoResize(): void {
    if (!autoResize?.height && !autoResize?.width) return

    const selector = autoResize.element || 'body'
    const element = document.querySelector(selector)
    if (!element) {
      logger.warn('Auto-resize element not found:', selector)
      return
    }

    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const payload: Record<string, number> = {}
        if (autoResize.height) {
          payload.height = Math.ceil(entry.contentRect.height)
        }
        if (autoResize.width) {
          payload.width = Math.ceil(entry.contentRect.width)
        }
        const message: BridgeMessage = {
          __afterpay_bridge__: true,
          tag,
          id: generateId(),
          type: MessageType.RESIZE,
          payload,
          timestamp: Date.now(),
        }
        parentWindow!.postMessage(message, parentOrigin)
      }
    })

    resizeObserver.observe(element)
    logger.debug('Auto-resize enabled for', selector)
  }

  function handleMessage(event: MessageEvent): void {
    if (parentOrigin !== '*' && event.origin !== parentOrigin) return
    if (!isBridgeMessage(event.data, tag)) return

    const message = event.data as BridgeMessage
    logger.debug('Received:', message.type, message.id)

    switch (message.type) {
      case MessageType.INIT_PROPS:
        applyProps(message.payload as Record<string, unknown>)
        break

      case MessageType.UPDATE_PROPS:
        applyProps(message.payload as Record<string, unknown>)
        for (const listener of updateListeners) {
          listener(currentProps)
        }
        break

      case MessageType.FUNCTION_RESULT: {
        const resultPayload = (message as BridgeResponse).payload as FunctionResultPayload
        const pending = pendingCalls.get(resultPayload.callId)
        if (pending) {
          pendingCalls.delete(resultPayload.callId)
          pending.resolve(resultPayload.result)
        }
        break
      }

      case MessageType.FUNCTION_ERROR: {
        const errorPayload = (message as BridgeResponse).payload as FunctionErrorPayload
        const pendingErr = pendingCalls.get(errorPayload.callId)
        if (pendingErr) {
          pendingCalls.delete(errorPayload.callId)
          pendingErr.reject(new Error(errorPayload.error))
        }
        break
      }

      case MessageType.DESTROY:
        window.removeEventListener('message', handleMessage)
        resizeObserver?.disconnect()
        pendingCalls.clear()
        logger.info('Destroyed by parent')
        break

      default:
        logger.debug('Unhandled message type:', message.type)
    }
  }

  window.addEventListener('message', handleMessage)

  // Send CHILD_READY and wait for INIT_PROPS
  const readyPromise = new Promise<void>((resolve) => {
    const checkProps = (event: MessageEvent): void => {
      if (!isBridgeMessage(event.data, tag)) return
      if (event.data.type === MessageType.INIT_PROPS) {
        window.removeEventListener('message', checkProps)
        resolve()
      }
    }
    window.addEventListener('message', checkProps)
  })

  // Send CHILD_READY
  const readyMessage: BridgeMessage = {
    __afterpay_bridge__: true,
    tag,
    id: generateId(),
    type: MessageType.CHILD_READY,
    timestamp: Date.now(),
  }
  parentWindow.postMessage(readyMessage, parentOrigin)
  logger.debug('Sent CHILD_READY')

  await withTimeout(readyPromise, timeout, 'Waiting for INIT_PROPS')

  // Set up auto-resize after props are received
  setupAutoResize()

  logger.info('Connected')

  return {
    props: currentProps,
    onPropsUpdate(cb: (props: Record<string, unknown>) => void): void {
      updateListeners.push(cb)
    },
  }
}
